import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { getSignLinkMeta, signLinkViewUrl, submitRemoteSignature } from '@/api/jobReports';
import { describeApiError } from '@/components/reports/envelope/envelopeModel';
import { SignatureCaptureForm, REPORT_SIGNATURE_CONSENT } from '@/components/signatures/SignatureCaptureForm';
import { SignPageShell, SignOutcomeCard, fmtDay } from '@/components/signatures/remote/SignPageShell';
import { SignAcknowledgements, missingRequired } from '@/components/signatures/remote/SignAcknowledgements';
import { SignRequestHeader } from '@/components/signatures/remote/SignRequestHeader';

export const CONSENT_TEXT = REPORT_SIGNATURE_CONSENT;
const CONTACT_LINE = 'Please contact the company that sent you this link.';
const DEAD_TITLES = {
  link_expired: 'This signing link has expired',
  envelope_voided: 'This signature request was cancelled',
  report_locked: 'This report is already signed',
};

/** Map a meta/submit error to the page state it lands on (null = retryable). */
export function phaseForError(err) {
  const e = describeApiError(err);
  if (e.status === 409 && e.code === 'already_signed') {
    return { kind: 'already_signed', signedAt: err.response.data.detail.signed_at };
  }
  if (e.status === 410 || e.status === 404 || (e.status === 409 && e.code === 'report_locked')) {
    return { kind: 'dead', title: DEAD_TITLES[e.code] || 'This signing link isn’t valid', message: e.message };
  }
  return null;
}

function Outcome({ phase, meta }) {
  if (phase.kind === 'dead') {
    return (
      <SignOutcomeCard tone="dead" title={phase.title} testId="sign-dead-end">
        <p data-testid="sign-dead-message">{phase.message}</p>
        <p>{CONTACT_LINE}</p>
      </SignOutcomeCard>
    );
  }
  if (phase.kind === 'already_signed') {
    return (
      <SignOutcomeCard tone="done" title="You've already signed" testId="sign-already-signed">
        <p>Your signature{meta?.report?.title ? ` on ${meta.report.title}` : ''} was recorded on{' '}
          <strong className="text-[#111417]" data-testid="sign-already-signed-date">{fmtDay(phase.signedAt)}</strong>.</p>
        <p>There&apos;s nothing else you need to do.</p>
      </SignOutcomeCard>
    );
  }
  return (
    <SignOutcomeCard tone="success" title="Thank you — you've signed" testId="sign-success">
      <p data-testid="sign-success-message">
        {phase.locked
          ? 'Every party has now signed. The final signed copy is on its way to your inbox.'
          : "You'll receive the final signed copy by email when all parties have signed."}
      </p>
    </SignOutcomeCard>
  );
}

function SignForm({ meta, token, onDone }) {
  const [accepted, setAccepted] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const acks = meta.acknowledgements || [];
  const blocked = missingRequired(acks, accepted).length > 0;

  const submit = async ({ name, data_url, signature_type }) => {
    setSubmitting(true);
    setError('');
    try {
      const res = await submitRemoteSignature(token, {
        name, data_url, signature_type,
        accepted_acknowledgements: acks.filter((a) => accepted[a.id]).map((a) => a.id),
      });
      onDone({ kind: 'success', locked: !!res?.report_locked });
    } catch (e) {
      const next = phaseForError(e);
      if (next) onDone(next);
      else setError(describeApiError(e).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SignAcknowledgements items={acks} accepted={accepted} onToggle={(id, v) => setAccepted((p) => ({ ...p, [id]: v }))} />
      <section className="bg-white border border-[#D5D8DD] rounded-sm">
        <header className="px-4 sm:px-6 py-3 border-b border-[#E6E8EC]">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-[#5C6370]">Step {acks.length ? 3 : 2}</div>
          <h2 className="text-base font-bold text-[#111417]">Sign</h2>
        </header>
        <div className="p-4 sm:p-6 space-y-4">
          {blocked && <p className="text-xs text-[#9A7326]" data-testid="sign-acks-required">Check every required acknowledgement above to sign.</p>}
          {error && (
            <div role="alert" data-testid="sign-submit-error"
              className="p-3 text-sm rounded-sm border border-[#B4463A]/20 border-l-4 border-l-[#B4463A] bg-[#B4463A]/10 text-[#8C332A]">
              <p>{error}</p>
              <p className="text-xs mt-1">Your signature is still here — tap Sign report to try again.</p>
            </div>
          )}
          <SignatureCaptureForm
            onSubmit={submit} submitting={submitting} submitLabel="Sign report"
            consentText={meta.consent_text || CONSENT_TEXT} initialName={meta.signer?.name || ''}
            showEmail={false} blocked={blocked}
          />
        </div>
      </section>
    </>
  );
}

/** Loop W2 — public `/sign-report/:token` page for an envelope signer. */
export default function ReportSignPage() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [phase, setPhase] = useState({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    getSignLinkMeta(token)
      .then((m) => {
        if (cancelled) return;
        setMeta(m);
        setPhase(m.state === 'already_signed' ? { kind: 'already_signed', signedAt: m.signed_at } : { kind: 'ready' });
      })
      .catch((e) => {
        if (cancelled) return;
        setPhase(phaseForError(e) || { kind: 'dead', title: 'We couldn’t load this signing request', message: describeApiError(e).message });
      });
    return () => { cancelled = true; };
  }, [token]);

  return (
    <SignPageShell companyName={meta?.company?.name}>
      {phase.kind === 'loading' && (
        <div className="flex items-center justify-center gap-2 py-24 text-sm text-[#5C6370]" data-testid="sign-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading your signing request…
        </div>
      )}
      {phase.kind === 'ready' && meta && (
        <>
          <SignRequestHeader meta={meta} viewUrl={signLinkViewUrl(token)} />
          <SignForm meta={meta} token={token} onDone={setPhase} />
        </>
      )}
      {['dead', 'already_signed', 'success'].includes(phase.kind) && <Outcome phase={phase} meta={meta} />}
    </SignPageShell>
  );
}
